import { useEffect, useState } from 'react';

import { tss } from 'tss-react/mui';

import { ChevronLeftOutlined } from '@mui/icons-material';
import {
  CircularProgress,
  MenuList,
  ListSubheader,
  Tooltip,
  ListItemIcon,
  IconButton,
  Typography,
} from '@mui/material';

import { sortFiles } from '@/utils/file/fileUtils';

import type { File } from './FileMenuItem';
import { FileMenuItem } from './FileMenuItem';

interface Props {
  selected: string | null | undefined;
  onChange: (file: File | null | undefined) => void;
  onChangeParent: (fileId: string | null | undefined) => void;
  loading: boolean;
  disableItemWithReason?: (file: File) => null | undefined | string;
  files: ReadonlyArray<File>;
  rootOwnerName: string;
  rootOwnerId: string;
  parent: null | Readonly<
    File & {
      owner: null | Readonly<{
        _id: string | null | undefined;
        name: string | null | undefined;
      }>;
    }
  >;
}

const useStyles = tss.create(({ theme }) => {
  const unit = parseInt(theme.spacing(1).replace('px', ''), 10);
  const extension = {
    paddingRight: unit,
    paddingLeft: unit,
    marginRight: -unit,
    marginLeft: -unit,
  };

  return {
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    header: {
      ...extension,
      paddingBottom: unit,
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      alignItems: 'center',
      outline: 'none',
    },

    iconButton: {
      width: unit * 4,
      height: unit * 4,
      marginRight: unit,
      padding: 0,
    },
  };
});

export const FilePickerView = ({
  loading,
  files,
  disableItemWithReason,
  selected,
  onChange,
  onChangeParent,
  parent,
  rootOwnerName,
  rootOwnerId,
}: Props) => {
  const { classes } = useStyles();

  const [sortedFiles, setSortedFiles] = useState(() =>
    sortFiles(files, 'FILENAME'),
  );

  useEffect(() => {
    setSortedFiles(sortFiles(files, 'FILENAME'));
  }, [files]);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  const parentFileOwnerId = parent?.owner?._id;
  const parentFileOwnerName = parent?.owner?.name;

  const parentIsRestricted = rootOwnerId !== parentFileOwnerId;

  const parentOwnerId = parentFileOwnerId || rootOwnerId;

  return (
    <div>
      <MenuList dense>
        <ListSubheader disableGutters className={classes.header}>
          {parent ? (
            <>
              <Tooltip title="Back to parent folder">
                <ListItemIcon>
                  <IconButton
                    classes={{
                      root: classes.iconButton,
                    }}
                    onClick={() => onChangeParent(parent?.parent?._id)}
                    size="large"
                  >
                    <ChevronLeftOutlined />
                  </IconButton>
                </ListItemIcon>
              </Tooltip>
              <div>
                <Typography variant="subtitle1">
                  {parent.filename || 'Unknown'}
                </Typography>
                {parentIsRestricted && (
                  <Typography variant="caption" color="error">
                    This folder is only visible to members of{' '}
                    {parentFileOwnerName}
                  </Typography>
                )}
              </div>
            </>
          ) : (
            <Typography variant="subtitle1">{rootOwnerName}</Typography>
          )}
        </ListSubheader>
        {sortedFiles.map((file) => {
          const { _id } = file;

          const tooltipText = disableItemWithReason
            ? disableItemWithReason(file)
            : undefined;

          const restricted = (file.owner && file.owner._id) !== parentOwnerId;

          const isDisabled = Boolean(tooltipText);

          return (
            <FileMenuItem
              tooltipText={tooltipText}
              key={_id || undefined}
              file={file}
              selected={_id === selected}
              disabled={isDisabled}
              onChangeParent={onChangeParent}
              onChange={onChange}
              restricted={restricted}
            />
          );
        })}
      </MenuList>
    </div>
  );
};
